/**
 * The items datasheet spec (canvas frame 5, display-options P5): ITEM · CATEGORY ·
 * SOURCE · LOCATION · <each select custom field> · STATE, default-grouped
 * Category ▸ Status. Desk-only, over the `displayAll` projection — the flat paged list
 * stays the phone screen and the over-cap fallback.
 *
 * THE CUSTOM FIELDS ARE FIRST-CLASS (spec §6): every SELECT-kind field the tenant defines
 * becomes both a column (frame 5's Size) and a group axis, from the same `intakeMeta`
 * vocabulary the filter sheet reads — nothing is special-cased to "Size".
 */
import { formatDay, todayISO } from '@mononoke/shared';
import { routePath } from '../platform/router.js';
import { directed } from '../components/sort-direction.js';
import type { DisplayItemRow } from '../../../api/src/catalog/items.js';
import { STATUS_LABELS, STATUS_OPTIONS } from './item-filters.js';
import { sanitizeConfig, type DisplayConfig, type EntityDisplaySpec, MAX_LEVELS, PHONE_MAX_LEVELS } from '../display/engine.js';
import { loadDisplayState, type StoredDisplay } from '../display/display-state.js';
import type { ColumnDef } from '../display/datasheet.js';

/**
 * EVERY custom field, of EVERY kind, is an axis (0.1.504; maintainer, 10 Sep 2026: "we should be
 * able to group and sort on every existing item field option" — after a user asked how to group by
 * brand and why colour would not sort). `kind` is the API's `FIELD_KINDS` word (`text` · `number` ·
 * `price` · `date` · `url` · `single_select` · `multi_select`); it decides the comparator and the
 * group keys below. Until 0.1.504 only the two select kinds reached this list.
 */
export interface ItemFieldDef {
  key: string;
  label: string;
  kind: string;
}

/** The API joins a multi-select's values with this (`items.ts`, the fields join); splitting on it
 *  is what puts a row in EVERY value's group, as project bindings do. */
const MULTI_SEP = ' · ';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A DATE field groups by MONTH (the deadline axis's bucket shape, ruling R2 a). The key is
 * `YYYY-MM · Mon` — the numeric prefix keeps alpha order chronological (the engine orders group
 * KEYS and has no label-for-key), the month name keeps the rail readable. No Date object: a
 * calendar string is sliced, never parsed (the date rule in CLAUDE.md).
 */
function monthKey(iso: string): string {
  const name = MONTHS[Number(iso.slice(5, 7)) - 1];
  return name === undefined ? iso.slice(0, 7) : `${iso.slice(0, 7)} · ${name}`;
}

/** The group keys a row contributes on a field axis, by kind. Empty → no key → "No <label>". */
function fieldKeys(f: ItemFieldDef, r: DisplayItemRow): string[] {
  const v = r.fields[f.label];
  if (v === undefined || v === '') return [];
  if (f.kind === 'multi_select') return v.split(MULTI_SEP).filter((s) => s !== '');
  if (f.kind === 'date') return [monthKey(v)];
  return [v];
}

/** A NUMERIC axis with the text axis's null rule: rows without a number sit last both ways. */
function byNumber(get: (r: DisplayItemRow) => string | null) {
  const num = (r: DisplayItemRow): number | null => {
    const s = get(r);
    if (s === null || s === '') return null;
    const n = Number(s.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };
  return (a: DisplayItemRow, b: DisplayItemRow, dir: 'asc' | 'desc'): number => {
    const av = num(a);
    const bv = num(b);
    if (av === null || bv === null) {
      if (av !== bv) return av === null ? 1 : -1;
    } else if (av !== bv) {
      return directed(av - bv, dir);
    }
    return directed(a.name.localeCompare(b.name), dir);
  };
}

/** The comparator for a field's sort axis, by kind: numbers and prices numerically; text, url,
 *  dates (ISO strings compare as time) and selects as text. */
function fieldCompare(f: ItemFieldDef) {
  const get = (r: DisplayItemRow): string | null => {
    const v = r.fields[f.label];
    return v === undefined || v === '' ? null : v;
  };
  return f.kind === 'number' || f.kind === 'price' ? byNumber(get) : byText(get);
}

const STATUS_ORDER = STATUS_OPTIONS.map((s) => s.value);

/**
 * A TEXT AXIS with the deadline axis's null rule (0.1.499): rows without the value sit LAST in
 * both directions, and every axis ends in the name so the order is total. Brand, Category,
 * Source and Location are the same comparator over a different field; "Last edited" too.
 */
function byText(get: (r: DisplayItemRow) => string | null) {
  return (a: DisplayItemRow, b: DisplayItemRow, dir: 'asc' | 'desc'): number => {
    const av = get(a);
    const bv = get(b);
    if (av === null || bv === null) {
      if (av !== bv) return av === null ? 1 : -1;
    } else {
      const cmp = directed(av.localeCompare(bv), dir);
      if (cmp !== 0) return cmp;
    }
    return directed(a.name.localeCompare(b.name), dir);
  };
}

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export function itemsDisplaySpec(fieldDefs: ItemFieldDef[]): EntityDisplaySpec<DisplayItemRow> {
  return {
    groupAxes: [
      {
        id: 'category',
        label: 'Category',
        keysOf: (r) => (r.categoryPath === null ? [] : [r.categoryPath]),
        orderModes: ['alpha'],
        emptyLabel: 'No category',
      },
      {
        id: 'status',
        label: 'Status',
        keysOf: (r) => [statusLabel(r.status)],
        // The filter sheet's own order (flagged deliberately first) IS this workflow.
        workflow: STATUS_ORDER.map(statusLabel),
        orderModes: ['workflow', 'alpha'],
        emptyLabel: 'No status',
        // Frame 56's toned level-2 rail (phone only): the rail wears the status's tone.
        tone: (key) => statusTone(STATUS_OPTIONS.find((o) => statusLabel(o.value) === key)?.value ?? ''),
      },
      {
        id: 'source',
        label: 'Source',
        keysOf: (r) => (r.sourceName === null ? [] : [r.sourceName]),
        orderModes: ['alpha'],
        emptyLabel: 'No source yet',
      },
      {
        id: 'location',
        label: 'Location',
        keysOf: (r) => (r.locationName === null ? [] : [r.locationName]),
        orderModes: ['alpha'],
        emptyLabel: 'No location',
      },
      /* BRAND as a group level (0.1.504; a user: "Kan ik groeperen per brand?"). It had a sort
         axis since 0.1.499 and nothing else. */
      {
        id: 'brand',
        label: 'Brand',
        keysOf: (r) => (r.brand === null || r.brand === '' ? [] : [r.brand]),
        orderModes: ['alpha'],
        emptyLabel: 'No brand',
      },
      ...fieldDefs.map((f) => ({
        id: `field:${f.key}`,
        label: f.label,
        keysOf: (r: DisplayItemRow) => fieldKeys(f, r),
        orderModes: ['alpha' as const],
        emptyLabel: `No ${f.label.toLowerCase()}`,
      })),
    ],
    sortAxes: [
      {
        id: 'name',
        label: 'Name',
        naturalDir: 'asc',
        compare: (a, b, dir) => directed(a.name.localeCompare(b.name), dir),
      },
      {
        id: 'deadline',
        label: 'Deadline',
        naturalDir: 'asc',
        compare: (a, b, dir) => {
          const ad = a.returnDueOn;
          const bd = b.returnDueOn;
          if (ad === null || bd === null) {
            if (ad !== bd) return ad === null ? 1 : -1;
          } else {
            const cmp = directed(ad.localeCompare(bd), dir);
            if (cmp !== 0) return cmp;
          }
          return directed(a.name.localeCompare(b.name), dir);
        },
      },
      {
        id: 'status',
        label: 'Status',
        naturalDir: 'asc',
        compare: (a, b, dir) => {
          const cmp = directed(STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status), dir);
          return cmp !== 0 ? cmp : directed(a.name.localeCompare(b.name), dir);
        },
      },
      /**
       * THE RECENCY AXES (0.1.499; maintainer, 9 Sep 2026: "sort on newest added items, I want that
       * option back"). "Added" is the catalogue instant — its natural direction is newest first,
       * the flat list's `recent` sort. "Last edited" is the latest edit or movement; an untouched
       * row sits last either way. Both are RFC 3339 strings with one offset, so the string compare
       * is the time compare.
       */
      {
        id: 'added',
        label: 'Added',
        naturalDir: 'desc',
        compare: (a, b, dir) => {
          const cmp = directed(a.createdAt.localeCompare(b.createdAt), dir);
          return cmp !== 0 ? cmp : a.name.localeCompare(b.name);
        },
      },
      {
        id: 'edited',
        label: 'Last edited',
        naturalDir: 'desc',
        compare: byText((r) => r.lastEditedAt),
      },
      // The four text axes (0.1.499): what the sheet already shows as columns and group levels,
      // now orderable within a group too.
      { id: 'brand', label: 'Brand', naturalDir: 'asc', compare: byText((r) => r.brand) },
      { id: 'category', label: 'Category', naturalDir: 'asc', compare: byText((r) => r.categoryPath) },
      { id: 'source', label: 'Source', naturalDir: 'asc', compare: byText((r) => r.sourceName) },
      { id: 'location', label: 'Location', naturalDir: 'asc', compare: byText((r) => r.locationName) },
      /* EVERY custom field sorts (0.1.504; a user: "Ik kan niet sorteren op colour") — by kind, see
         `fieldCompare`. Before this the sort list was fixed and a field, select or not, had no axis. */
      ...fieldDefs.map((f) => ({
        id: `field:${f.key}`,
        label: f.label,
        naturalDir: 'asc' as const,
        compare: fieldCompare(f),
      })),
    ],
  };
}

/**
 * THE RESTING VIEW IS CATEGORY ▸ STATUS, NAME A→Z (0.1.522, maintainer 1 Oct 2026: "The items tab
 * should default group by Category and status, sort name ascending"). This REVERSES 0.1.487's flat
 * start (8 Sep: "every display starts without any selected groups, sorts, or filters") — frame 5's
 * own Category ▸ Status is back as the landing view. The sort is a stated default now, not merely
 * the order underneath, so it carries `sortChosen` and the chip reads "Name ↑", never "None".
 * Every sheet's default is pinned in display/defaults.test.ts.
 */
export const ITEMS_DISPLAY_DEFAULT: DisplayConfig = {
  levels: [
    { axis: 'category', orderMode: 'alpha', dir: 'asc' },
    { axis: 'status', orderMode: 'workflow', dir: 'asc' },
  ],
  sortAxis: 'name',
  sortDir: 'asc',
  sortChosen: true,
};

/**
 * TWO SURFACES, TWO KEYS (phone display P3, D1): the desk sheet under `items`, the phone list
 * under `items.phone` with its own cap. The phone default is the desk default run through the
 * phone cap (D2, one derivation). Both re-seed when intakeMeta lands.
 */
export function initialItemsDisplay(fieldDefs: ItemFieldDef[], variant: 'desk' | 'phone' = 'desk'): StoredDisplay {
  const spec = itemsDisplaySpec(fieldDefs);
  const cap = variant === 'phone' ? PHONE_MAX_LEVELS : MAX_LEVELS;
  const fallback = sanitizeConfig(ITEMS_DISPLAY_DEFAULT, spec, ITEMS_DISPLAY_DEFAULT, cap);
  const stored = loadDisplayState(variant === 'phone' ? 'items.phone' : 'items');
  return {
    v: 3,
    config: sanitizeConfig(stored?.config, spec, fallback, cap),
    collapsed: stored?.collapsed ?? [],
  };
}

/** The row's state tone, for the compact row's right column and the toned status rails. */
export function itemStatusTone(status: string): 'ok' | 'warn' | 'info' | 'inert' {
  return statusTone(status);
}

function statusTone(status: string): 'ok' | 'warn' | 'info' | 'inert' {
  if (status === 'available') return 'ok';
  if (status === 'checked_out' || status === 'in_custody') return 'info';
  if (status === 'flagged') return 'warn';
  return 'inert';
}

/** Calendar days from one YYYY-MM-DD to another (UTC arithmetic on calendar dates only). */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

export function itemEdge(r: DisplayItemRow): string {
  const tone = statusTone(r.status);
  if (tone === 'info') return 'var(--info-edge)';
  if (tone === 'warn') return 'var(--warn-edge)';
  if (tone === 'ok') return 'transparent';
  return 'var(--pending-soft)';
}

export function itemColumns(
  thumbs: Record<string, string>,
  config: DisplayConfig,
  fieldDefs: ItemFieldDef[],
  /** Items with an unsent queued change — the sheet flies the same pill the flat row
   *  does (G10: a queued change lights its row, whichever list is showing). */
  pendingIds: ReadonlySet<string>,
): ColumnDef<DisplayItemRow>[] {
  const today = todayISO();
  const grouped = new Set(config.levels.map((l) => l.axis));
  const all: ColumnDef<DisplayItemRow>[] = [
    {
      id: 'item',
      label: 'Item',
      track: 'minmax(0, 2.2fr)',
      render: (r) => {
        const url = r.photoKey === null ? undefined : thumbs[r.photoKey];
        return (
          <span className="ds-lead">
            {url !== undefined ? (
              <img className="ds-thumb" src={url} alt="" loading="lazy" />
            ) : (
              <span className="ds-thumb" aria-hidden="true" />
            )}
            {/* A REAL LINK, like the flat row's `.item-link`: middle-click, long-press and
                keyboard all work, and getByRole('link') means one thing at both widths.
                preventDefault + bubble: the ROW's handler owns navigation (and the
                two-click arming guard), so the anchor never double-navigates. */}
            <a
              className="item-link ds-name"
              href={routePath({ name: 'item', id: r.id })}
              onClick={(e) => e.preventDefault()}
            >
              {r.name}
            </a>
            {pendingIds.has(r.id) && (
              <span className="item-pending" role="status">
                Waiting to send
              </span>
            )}
          </span>
        );
      },
    },
    /* BRAND IS A COLUMN OF ITS OWN (0.1.504, ruling R1 a) and LEFT the lead cell, where it rode as
       "· brand" since the datasheet build. One fact, one place: group by brand and the grouped-axis
       rule drops this column — the rail already says it. */
    {
      id: 'brand',
      label: 'Brand',
      axisId: 'brand',
      track: 'minmax(0, 0.9fr)',
      render: (r) =>
        r.brand === null || r.brand === '' ? <span className="ds-inert">—</span> : <span className="ds-muted">{r.brand}</span>,
    },
    {
      id: 'category',
      label: 'Category',
      axisId: 'category',
      track: 'minmax(0, 1.1fr)',
      render: (r) =>
        r.categoryPath === null ? <span className="ds-inert">—</span> : <span className="ds-muted">{r.categoryPath}</span>,
    },
    {
      id: 'source',
      label: 'Source',
      axisId: 'source',
      track: 'minmax(0, 1.1fr)',
      render: (r) =>
        r.sourceName === null ? <span className="ds-inert">—</span> : <span className="ds-muted">{r.sourceName}</span>,
    },
    {
      id: 'location',
      label: 'Location',
      axisId: 'location',
      track: 'minmax(0, 1.1fr)',
      render: (r) =>
        r.locationName === null ? <span className="ds-inert">—</span> : <span className="ds-muted">{r.locationName}</span>,
    },
    /* A FIELD IS A COLUMN WHILE IT IS ACTIVE (0.1.504, ruling R3 a): the sort axis, or a group
       level — and a group level is then dropped by the grouped-axis rule below, so in practice the
       column shows for the field being SORTED on and the rail speaks for the field being GROUPED
       by. Until 0.1.504 every select field was a column always; with a dozen fields that is a
       scroll, and the sheet has no column picker (the door if "I only want to SEE it" comes). */
    ...fieldDefs
      .filter((f) => config.sortAxis === `field:${f.key}` || grouped.has(`field:${f.key}`))
      .map((f): ColumnDef<DisplayItemRow> => ({
        id: `field:${f.key}`,
        label: f.label,
        axisId: `field:${f.key}`,
        track: 'minmax(0, 0.8fr)',
        render: (r) => {
          const v = r.fields[f.label];
          return v === undefined || v === '' ? <span className="ds-inert">—</span> : <span className="ds-muted">{v}</span>;
        },
      })),
    {
      id: 'deadline',
      label: 'Deadline',
      track: 'minmax(0, 0.8fr)',
      render: (r) => {
        if (r.returnDueOn === null) return <span className="ds-inert">—</span>;
        const overdue = r.returnDueOn < today;
        // Within the week reads in the warn tone (0.1.457) — the boxes sheet's own rule.
        const soon = !overdue && daysBetween(today, r.returnDueOn) <= 7;
        return (
          <span className={overdue ? 'ds-deadline ds-deadline-overdue' : soon ? 'ds-deadline ds-deadline-soon' : 'ds-deadline'}>
            {overdue && <span className="ds-dot" aria-hidden="true" />}
            {formatDay(r.returnDueOn)}
          </span>
        );
      },
    },
    {
      // NO axisId on purpose (frame 5): even grouped by Status the cell earns its place —
      // a flagged row shows the flag's REASON as the exception pill, not the bare word.
      id: 'state',
      label: 'State',
      track: 'minmax(0, 0.9fr)',
      render: (r) => {
        if (r.status === 'flagged' && r.flagReason !== null) {
          return <span className="ds-pill-alert">{r.flagReason}</span>;
        }
        return <span className={`ds-state ds-state-${statusTone(r.status)}`}>{statusLabel(r.status)}</span>;
      },
    },
  ];
  return all.filter((c) => c.axisId === undefined || !grouped.has(c.axisId));
}
